import { INTERCOM_TRIGGER } from '@sparklayer-io/spark-dash-core/constants/intercom';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { t } from 'i18next';
import type { ReactNode } from 'react';
import { useUser } from '../context/page-context';

/**
 * Which parts of a setting row are limited to SparkLayer staff. Undefined means everyone.
 * - `read-write`: hidden from merchants, badged for staff. Any row, handled by SettingRow.
 * - `write`: merchants see it read-only with a route to support. Toggle rows only.
 */
export type StaffAccess = 'read-write' | 'write';

/** Locks a `write` row for merchants and explains why, with a link to support. */
export function useStaffWriteLock({
  staff,
  contactTrigger = INTERCOM_TRIGGER.contact,
}: {
  staff?: StaffAccess;
  /** Intercom trigger for the support link. */
  contactTrigger?: string;
}): { locked: boolean; helpText?: ReactNode } {
  const { isSparkStaff } = useUser();

  if (staff !== 'write' || isSparkStaff) {
    return { locked: false };
  }

  return {
    locked: true,
    helpText: (
      <span className="flex items-center gap-1">
        {t('components:staff_setting.help')}
        <button
          type="button"
          className={cn(
            'inline-flex cursor-pointer items-center font-medium text-spark-accent hover:text-spark-accent-hover',
            contactTrigger,
          )}
        >
          {t('components:staff_setting.contact')}
        </button>
      </span>
    ),
  };
}
